import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Target, Calendar, Users, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAnalytics } from '@/hooks/useAnalytics';
import SEOSchema from '@/components/SEOSchema';

const WhatIsPACTFramework = () => {
  const navigate = useNavigate();
  const { trackCTAClick, trackPageView } = useAnalytics();

  useEffect(() => {
    // SEO optimization for this specific page
    document.title = "What is PACT Framework? | Show Up! - Don't Overthink Habit Tracker";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Learn about the PACT Framework - the minimalist approach to building habits. Perfect for those who don't want to overthink habit formation. Show up again with simple commitments.");
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "PACT framework explained, what is PACT habit tracker, minimalist habit formation, don't overthink habits, simple commitment method, habit building framework, show up again method");
    }

    trackPageView('What is PACT Framework');
  }, [trackPageView]);

  const handleGetStarted = () => {
    trackCTAClick('pact-framework-page');
    navigate('/early-access');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const pactSteps = [
    {
      letter: 'P',
      title: 'Pick One Thing',
      description: "Don't overthink it. Choose one simple commitment you can show up for daily.",
      icon: Target,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      letter: 'A', 
      title: 'Act Daily',
      description: "Show up again. Take the smallest possible action every single day.",
      icon: Calendar,
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      letter: 'C',
      title: 'Connect',
      description: "Share your commitment. Let others witness your daily showing up.",
      icon: Users,
      color: 'bg-green-100 text-green-600'
    },
    {
      letter: 'T',
      title: 'Track Progress',
      description: "See your streak grow. Celebrate showing up again and again.",
      icon: TrendingUp,
      color: 'bg-blue-100 text-blue-600'
    }
  ];

  return (
    <>
      {/* SEO Schema for this page */}
      <SEOSchema 
        type="website"
        name="What is PACT Framework? | Show Up! - Habit Tracker"
        description="Learn about the PACT Framework - the minimalist approach to building habits. Perfect for those who don't want to overthink habit formation."
        url={typeof window !== 'undefined' ? window.location.href : "https://yourapp.lovable.app/what-is-pact-framework"}
      />

      <div className="min-h-screen bg-gradient-to-br from-purple-500 via-purple-400 to-indigo-500">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-white/95 via-purple-100/90 to-purple-600/90 backdrop-blur-sm border-b border-white/10">
          <div className="container max-w-7xl mx-auto px-4 py-5">
            <div className="flex justify-between items-center">
              <Button 
                onClick={handleBackToHome}
                variant="ghost"
                className="flex items-center gap-2 text-purple-900 hover:text-purple-700"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Button>
              
              <img 
                alt="Show Up! - Don't Overthink Habit Tracker Logo" 
                className="h-16 w-auto drop-shadow-lg object-contain" 
                src="/lovable-uploads/9b33affd-d213-4c54-b7d2-a74e48fe5ff5.png"
                loading="eager"
              />
              
              <Button 
                onClick={handleGetStarted} 
                className="bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-black px-6 py-3 rounded-xl shadow-lg hover:scale-105 transition-all duration-300"
              >
                Get Early Access
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pt-32 pb-16">
          <div className="container max-w-6xl mx-auto px-4">
            
            {/* Hero Section */}
            <div className="text-center mb-16">
              <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight">
                What is the
                <span className="block text-yellow-400">PACT Framework?</span>
              </h1>
              <p className="text-xl md:text-2xl text-white/90 font-medium max-w-4xl mx-auto leading-relaxed">
                The minimalist approach to building habits.<br />
                <span className="text-yellow-300">Don't overthink it. Show up again.</span>
              </p>
            </div>

            {/* Philosophy Section */}
            <Card className="mb-16 bg-white/10 backdrop-blur-sm border-white/20">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
                  The App That Comes Before Habits & Goals
                </h2>
                <div className="text-lg md:text-xl text-white/90 leading-relaxed space-y-4 max-w-4xl mx-auto">
                  <p>
                    Most habit trackers overwhelm you with complex systems, multiple habits, and detailed analytics. 
                    <strong className="text-yellow-300"> We believe that's exactly the problem.</strong>
                  </p>
                  <p>
                    PACT is different. It's about showing up with one simple commitment before you worry about 
                    building complex habits or chasing big goals.
                  </p>
                  <p className="text-yellow-300 font-semibold text-center text-2xl">
                    "Don't overthink it. Show up again."
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* PACT Steps */}
            <div className="mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-12">
                How PACT Works
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {pactSteps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all duration-300">
                      <CardContent className="p-8">
                        <div className="flex items-start gap-6">
                          <div className="flex-shrink-0">
                            <div className="w-16 h-16 bg-yellow-400 rounded-2xl flex items-center justify-center shadow-lg">
                              <span className="text-2xl font-black text-purple-900">{step.letter}</span>
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <Icon className="w-6 h-6 text-yellow-400" />
                              <h3 className="text-2xl font-bold text-white">{step.title}</h3>
                            </div>
                            <p className="text-white/80 text-lg leading-relaxed">{step.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Why PACT Works */}
            <Card className="mb-16 bg-white/10 backdrop-blur-sm border-white/20">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                  Why PACT Works When Other Methods Fail
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">No Overwhelm</h3>
                        <p className="text-white/80">One commitment at a time. No complex systems to maintain.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">Built for Consistency</h3>
                        <p className="text-white/80">Focus on showing up daily, not perfect performance.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">Social Accountability</h3>
                        <p className="text-white/80">Share your commitment with others who support your journey.</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">Minimalist Design</h3>
                        <p className="text-white/80">Clean interface that doesn't distract from your commitment.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">Before Goals</h3>
                        <p className="text-white/80">Build the foundation of showing up before setting big goals.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">Anti-Overthinking</h3>
                        <p className="text-white/80">Designed to prevent analysis paralysis and perfectionism.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA Section */}
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                Ready to Stop Overthinking?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Join 67+ people who are already showing up again with the PACT framework.
              </p>
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="bg-yellow-400 hover:bg-yellow-300 text-purple-900 font-black px-12 py-4 rounded-xl shadow-lg hover:scale-105 transition-all duration-300 text-xl"
              >
                Get Early Access Now
              </Button>
            </div>

          </div>
        </main>
      </div>
    </>
  );
};

export default WhatIsPACTFramework;